import { createRng, type RngState } from '../engine/rng.js';

export type MapId = 'core' | 'ruins' | 'magnetic';

export interface MapDefinition {
  id: MapId;
  nameKey: string;
  unlockRequirementDay: number;
  /** Previous map day record needed to unlock (0 = always). */
  unlockOnMap: MapId | null;
}

export const MAP_DEFINITIONS: Record<MapId, MapDefinition> = {
  core: {
    id: 'core',
    nameKey: 'map.core',
    unlockRequirementDay: 0,
    unlockOnMap: null,
  },
  ruins: {
    id: 'ruins',
    nameKey: 'map.ruins',
    unlockRequirementDay: 28,
    unlockOnMap: 'core',
  },
  magnetic: {
    id: 'magnetic',
    nameKey: 'map.magnetic',
    unlockRequirementDay: 35,
    unlockOnMap: 'ruins',
  },
};

export type TerrainCellKind = 'empty' | 'obstacle' | 'magnetic';

export interface TerrainSnapshot {
  width: number;
  height: number;
  /** Row-major index y * width + x */
  cells: TerrainCellKind[];
}

function cellIndex(x: number, y: number, width: number): number {
  return y * width + x;
}

function inBounds(x: number, y: number, width: number, height: number): boolean {
  return x >= 0 && y >= 0 && x < width && y < height;
}

/** Deterministic obstacle clusters for ruins map. */
export function generateRuinsTerrain(
  width: number,
  height: number,
  seed: number,
  margin = 1,
): TerrainSnapshot {
  const cells: TerrainCellKind[] = new Array(width * height).fill('empty');
  const rng = createRng(seed ^ 0x9e3779b9);

  const clusterCount = Math.max(3, Math.floor((width * height) / 80));
  for (let c = 0; c < clusterCount; c++) {
    const cx = rng.nextInt(margin, width - margin - 1);
    const cy = rng.nextInt(margin, height - margin - 1);
    const radius = rng.nextInt(1, 3);
    for (let dy = -radius; dy <= radius; dy++) {
      for (let dx = -radius; dx <= radius; dx++) {
        if (dx * dx + dy * dy > radius * radius + 0.5) continue;
        const x = cx + dx;
        const y = cy + dy;
        if (!inBounds(x, y, width, height)) continue;
        if (x < margin || y < margin || x >= width - margin || y >= height - margin) continue;
        cells[cellIndex(x, y, width)] = 'obstacle';
      }
    }
  }

  return { width, height, cells };
}

/** Magnetic zones + moderate obstacles. */
export function generateMagneticTerrain(
  width: number,
  height: number,
  seed: number,
  margin = 1,
): TerrainSnapshot {
  const base = generateRuinsTerrain(width, height, seed, margin);
  const rng = createRng(seed ^ 0x517cc1b7);
  const zoneCount = Math.max(2, Math.floor((width * height) / 120));

  for (let z = 0; z < zoneCount; z++) {
    const cx = rng.nextInt(margin + 1, width - margin - 2);
    const cy = rng.nextInt(margin + 1, height - margin - 2);
    const w = rng.nextInt(2, 4);
    const h = rng.nextInt(2, 4);
    for (let y = cy; y < cy + h && y < height - margin; y++) {
      for (let x = cx; x < cx + w && x < width - margin; x++) {
        if (!inBounds(x, y, width, height)) continue;
        const idx = cellIndex(x, y, width);
        if (base.cells[idx] !== 'obstacle') {
          base.cells[idx] = 'magnetic';
        }
      }
    }
  }

  return base;
}

export function generateMapTerrain(
  mapId: MapId,
  width: number,
  height: number,
  seed: number,
): TerrainSnapshot {
  switch (mapId) {
    case 'core':
      return {
        width,
        height,
        cells: new Array(width * height).fill('empty'),
      };
    case 'ruins':
      return generateRuinsTerrain(width, height, seed);
    case 'magnetic':
      return generateMagneticTerrain(width, height, seed);
    default:
      return generateMapTerrain('core', width, height, seed);
  }
}

/** Expand terrain when grid grows — new ring filled from generator. */
export function expandTerrain(
  prev: TerrainSnapshot,
  newWidth: number,
  newHeight: number,
  mapId: MapId,
  seed: number,
): TerrainSnapshot {
  if (newWidth === prev.width && newHeight === prev.height) return prev;
  const full = generateMapTerrain(mapId, newWidth, newHeight, seed + newWidth * 997 + newHeight);
  const offsetX = Math.floor((newWidth - prev.width) / 2);
  const offsetY = Math.floor((newHeight - prev.height) / 2);

  for (let y = 0; y < prev.height; y++) {
    for (let x = 0; x < prev.width; x++) {
      const nx = x + offsetX;
      const ny = y + offsetY;
      full.cells[cellIndex(nx, ny, newWidth)] = prev.cells[cellIndex(x, y, prev.width)];
    }
  }
  return full;
}

export type { RngState };
